import { BACKGROUND, BODY, BODY_CONTENT, CONTENT, DEFAULT_COLORS_BG, DEFAULT_COLORS_FONT, FONT, FOOTER, HEADER } from '@opengov/stories-shared/constants';
import { IStoryCustomColors } from '@opengov/stories-shared/types';
import { ISettingsColors, ITypeColors } from '../../types';
import {
  BLOCKQUOTE,
  LINK,
  LIST_ITEM,
  LIST_ITEM_CHILD,
  ORDERED_LIST,
  TABLE_CELL,
  TABLE_ROW,
  UNORDERED_LIST
} from '../constants';

// The regex searches for 'editable' in a node's className(@return string).
const editable = /editable(\s|$)/;
const colorsTypeByStoryContentType = {
  [HEADER]: HEADER,
  [BODY_CONTENT]: BODY,
  [FOOTER]: FOOTER
};

// A editor helper to standardize wrapping links.
export const wrapLink = (editor, href : string): void => {
  editor.wrapInline({
    type: LINK,
    data: { href }
  });

  editor.moveToEnd();
};

// A editor helper to standardize unwrapping links.
export const unwrapLink = (editor): void => {
  editor.unwrapInline(LINK);
};

export const findEditableParentKey = (document, block): string => {
  const blockClass = block.getIn(['data', 'className']);
  let key;

  if (blockClass && editable.test(blockClass)) {
    key = block.key;
  } else {
    const parent = document.getParent(block.key);
    if (parent) {
      return findEditableParentKey(document, document.getParent(block.key));
    }
  }

  return key;
};

export const getCurrentBlock = (editor) => {
  return editor.value.startBlock;
};

export const getCurrentBlockParent = (editor) => {
  const block = getCurrentBlock(editor);
  return editor.value.document.getParent(block.key);
};

export const hasEmptyParent = editor => {
  const parent = getCurrentBlockParent(editor);
  // empty line is treated as one node. this allows user to remove empty lines and bullet points.
  if ((parent.text.length === 0 && parent.nodes.size > 1) || parent.type === LIST_ITEM) return false;
  return parent.text.length === 0;
};

export const hasEditableParent = editor => {
  const parent = getCurrentBlockParent(editor);
  const parentClass = parent.getIn(['data', 'className']);
  return parentClass && (editable.test(parentClass) || parentClass.indexOf('logo') > -1);
};

// check for parent nodes which may not be of type div but are part of the editing experience.
export const hasSpecialParent = editor => {
  const parent = getCurrentBlockParent(editor);
  return (
    parent.type === LIST_ITEM ||
    parent.type === LIST_ITEM_CHILD ||
    parent.type === ORDERED_LIST ||
    parent.type === UNORDERED_LIST ||
    parent.type === BLOCKQUOTE ||
    parent.type === TABLE_ROW ||
    parent.type === TABLE_CELL
  );
};

export const willDeleteMedia = editor => {
  const block = getCurrentBlock(editor);
  const emptyBlock = block.text.length === 0;
  const parent = getCurrentBlockParent(editor);
  const emptyParent = parent.text.length === 0;
  const firstParentNode = parent.nodes.first();
  const isFirstChild = block.key === firstParentNode.key;

  return (
    isFirstChild &&
    !emptyParent &&
    !hasSpecialParent(editor) &&
    hasEditableParent(editor) &&
    emptyBlock &&
    editor.value.selection.isCollapsed
  );
};

export const willDeleteBlockBackward = editor => {
  const block = getCurrentBlock(editor);
  const emptyBlock = block.text.length === 0;
  const parent = getCurrentBlockParent(editor);
  const firstParentNode = parent.nodes.first();
  const isFirstChild = block.key === firstParentNode.key;

  return (
    isFirstChild &&
    !emptyBlock &&
    hasEditableParent(editor) &&
    editor.value.selection.start.offset === 0 &&
    editor.value.selection.isCollapsed
  );
};
export const willDeleteBlockForward = editor => {
  const block = getCurrentBlock(editor);
  const characterLength = block.text.length || 0;
  const parent = getCurrentBlockParent(editor);
  const lastParentNode = parent.nodes.last();
  const isLastChild = block.key === lastParentNode.key;

  return (
    isLastChild &&
    block.getOffsetAtRange(editor.value.selection) === characterLength &&
    editor.value.selection.isCollapsed
  );
};

export interface IGetColorsByStoryContentTypeArgs {
  storyContentType: string;
  colors: ISettingsColors;
  storyCustomColors: IStoryCustomColors;
  colorsSubtype?: typeof FONT | typeof BACKGROUND;
}
export interface ISubtypeColors {
  swatch: string[];
  customSwatch: string[];
  active: string;
}
export const getSubtypeColorsByStoryContentType = ({
  storyContentType,
  colors,
  storyCustomColors,
  colorsSubtype = FONT
}: IGetColorsByStoryContentTypeArgs): ISubtypeColors => {
  const defaultColors = colorsSubtype === FONT ? DEFAULT_COLORS_FONT : DEFAULT_COLORS_BG;
  const backwardCompatibleType = storyContentType === CONTENT ? BODY_CONTENT : storyContentType;
  const colorsType: string = colorsTypeByStoryContentType[backwardCompatibleType];
  const customSwatch: string[] = storyCustomColors[backwardCompatibleType][colorsSubtype];
  const typeColors: ITypeColors = colors[colorsType];

  if (typeColors && typeColors[colorsSubtype]) {
    return {
      swatch: typeColors[colorsSubtype].swatch,
      customSwatch,
      active: typeColors[colorsSubtype].active
    };
  }
  return {
    swatch: defaultColors,
    customSwatch,
    active: defaultColors[defaultColors.length - 1]
  };
};

// Remove HTML tags from a string.
export const removeHtmlTags = (str: string) => {
  return str.replace(/<[^>]*>/g, '');
}
